import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './ui/Button';
import './ActivityDeleteBlockedModal.css';

export interface IBlockerItem {
  _id: number;
  name: string;
}

/** What the 409 ACTIVITY_DELETE_BLOCKED response reports. */
export interface IDeleteBlockers {
  members: { total: number; items: IBlockerItem[] };
  courses: { total: number; items: IBlockerItem[] };
}

interface Props {
  isOpen: boolean;
  activityName: string;
  blockers: IDeleteBlockers;
  busy?: boolean;
  /** False hides the remove affordances for a role lacking members:write. */
  canRemoveMembers?: boolean;
  onClose: () => void;
  /** Omit memberIds to clear every enrollment. */
  onRemoveMembers: (memberIds?: number[]) => void;
  onDelete: () => void;
}

const ActivityDeleteBlockedModal: React.FC<Props> = ({
  isOpen,
  activityName,
  blockers,
  busy = false,
  canRemoveMembers = true,
  onClose,
  onRemoveMembers,
  onDelete,
}) => {
  const { t } = useTranslation();

  const { members, courses } = blockers;
  const clear = members.total === 0 && courses.total === 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('activities.deleteBlockedTitle')} size="medium">
      <div className="delete-blocked">
        <p className="delete-blocked__intro">
          {t('activities.deleteBlockedIntro', { name: activityName })}
        </p>

        {members.total > 0 && (
          <section>
            <div className="delete-blocked__section-head">
              <h3 className="delete-blocked__section-title">
                {t('activities.deleteBlockedMembers', { count: members.total })}
              </h3>
              {canRemoveMembers && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onRemoveMembers()}
                  disabled={busy}
                >
                  {t('activities.removeAllMembers', { count: members.total })}
                </Button>
              )}
            </div>
            <p className="delete-blocked__help">
              {canRemoveMembers
                ? t('activities.deleteBlockedMembersHelp')
                : t('activities.deleteBlockedNoMemberRight')}
            </p>
            <ul className="delete-blocked__list">
              {members.items.map((member) => (
                <li key={member._id} className="delete-blocked__row">
                  <span className="delete-blocked__name">{member.name}</span>
                  {canRemoveMembers && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onRemoveMembers([member._id])}
                      disabled={busy}
                    >
                      {t('activities.removeMember')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {members.total > members.items.length && (
              <p className="delete-blocked__more">
                {t('activities.deleteBlockedMore', { count: members.total - members.items.length })}
              </p>
            )}
          </section>
        )}

        {courses.total > 0 && (
          <section>
            <div className="delete-blocked__section-head">
              <h3 className="delete-blocked__section-title">
                {t('activities.deleteBlockedCourses', { count: courses.total })}
              </h3>
            </div>
            <p className="delete-blocked__help">{t('activities.deleteBlockedCoursesHelp')}</p>
            <ul className="delete-blocked__list">
              {courses.items.map((course) => (
                <li key={course._id} className="delete-blocked__row">
                  <span className="delete-blocked__name">{course.name}</span>
                  <Link to={`/admin/courses/${course._id}/edit`} className="btn btn--secondary btn--sm">
                    {t('common.edit')}
                  </Link>
                </li>
              ))}
            </ul>
            {courses.total > courses.items.length && (
              <p className="delete-blocked__more">
                {t('activities.deleteBlockedMore', { count: courses.total - courses.items.length })}
              </p>
            )}
          </section>
        )}

        {clear && <p className="delete-blocked__ready">{t('activities.deleteBlockedReady')}</p>}

        <div className="delete-blocked__actions">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={onDelete} loading={busy} disabled={!clear || busy}>
            {t('activities.deleteNow')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ActivityDeleteBlockedModal;
